'use strict'

import {
  CircularProgress,
  Tooltip
} from '@material-ui/core'
import PlaceIcon from '@material-ui/icons/Place'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import { formatMinutes } from './mapUtils'

const DAILY_OT_THRESHOLD_MIN = 480

/**
 * Desert Flora styled candidate card for Route Builder.
 * Matches .cand-card pattern: name/address, distance pill, delta minutes,
 * frequency tag, relief/due/OT badges, Focus + Add to Day buttons.
 */
export default function CandidateCard ({
  candidate, idx, pinColor,
  showSuggestionPins, remainingCapacityMin,
  selectedScenarioId, routeActionPending,
  handlePropertySelect, addToDay
}) {
  const exceeds = candidate.wouldCreateOt || candidate.serviceMinutes > remainingCapacityMin

  // Delta display
  const delta = candidate.delta
  const deltaColor = delta != null && delta < 0
    ? '#2f7a3e'
    : delta != null && delta > 0
      ? '#a9341d'
      : 'var(--df-ink-4, #6f7a6b)'
  const deltaTxt = delta != null && delta !== 0
    ? ((delta > 0 ? '+' : '') + delta + 'm')
    : null

  // Crew day capacity
  const crewDayStats = candidate.crewDayStats
  const capPct = crewDayStats ? Math.round(crewDayStats.capacityPct || ((crewDayStats.totalMin || 0) / DAILY_OT_THRESHOLD_MIN * 100)) : null

  return (
    <div
      className={'cand-card' + (exceeds ? ' disabled' : '')}
      style={{
        ...(showSuggestionPins ? { borderLeft: `3px solid ${pinColor}` } : {})
      }}
    >
      {/* Row 1: Name + address + distance pill */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 7 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {showSuggestionPins && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                backgroundColor: pinColor, color: '#fff',
                fontSize: 10, fontWeight: 700, lineHeight: 1
              }}>
                {idx + 1}
              </span>
            )}
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--df-ink, #1c2419)', lineHeight: 1.3 }}>
              {candidate.propertyName || `#${candidate.aspirePropertyId}`}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--df-ink-4, #97a094)', marginTop: 1 }}>
            {candidate.address || '-'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          {candidate.distanceMiles != null && (
            <span className="rw-pill pill-dist">{candidate.distanceMiles} mi</span>
          )}
        </div>
      </div>

      {/* Row 2: Delta + freq + crew label + capacity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {deltaTxt && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: deltaColor }}>{deltaTxt}</span>
        )}
        <span style={{ fontSize: 11, color: 'var(--df-ink-4, #97a094)' }}>
          {formatMinutes(candidate.serviceMinutes)}
        </span>
        {candidate.frequencyCode && (
          <span className="rw-pill pill-freq">{candidate.frequencyCode}</span>
        )}
        <span style={{
          fontSize: 10.5, color: 'var(--df-ink-4, #97a094)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          flex: 1, minWidth: 0
        }}>
          {candidate.assignedName}
        </span>
        {capPct != null && (
          <Tooltip
            title={`${crewDayStats.routeName || 'Crew'}: ${crewDayStats.stops || 0} stops · ${formatMinutes(crewDayStats.totalMin || 0)} / ${formatMinutes(DAILY_OT_THRESHOLD_MIN)} (${capPct}%)`}
            placement="top"
            arrow
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'default' }}>
              <span style={{
                display: 'inline-block', width: 32, height: 5, borderRadius: 99,
                background: 'var(--df-bg-2, #ece5d3)', overflow: 'hidden'
              }}>
                <span style={{
                  display: 'block', height: '100%', borderRadius: 99,
                  width: `${Math.min(100, capPct)}%`,
                  background: capPct > 90 ? '#a9341d' : capPct >= 70 ? '#b35d1a' : '#2f7a3e'
                }} />
              </span>
              <span style={{
                fontSize: 10, fontWeight: 600,
                color: capPct > 90 ? '#a9341d' : capPct >= 70 ? '#b35d1a' : '#2f7a3e'
              }}>
                {capPct > 100 ? `OT +${Math.round(crewDayStats.otMin || 0)}m` : `${capPct}%`}
              </span>
            </span>
          </Tooltip>
        )}
      </div>

      {/* Row 3: Badges (relief / due / OT risk) */}
      {(candidate.crewDayStats?.capacityPct > 95 || candidate.dueToday || candidate.wouldCreateOt || candidate.occurrenceCount > 1) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 9, flexWrap: 'wrap' }}>
          {candidate.crewDayStats?.capacityPct > 95 && (
            <span className="rw-pill pill-relief">relief</span>
          )}
          {candidate.dueToday && (
            <span className="rw-pill pill-due">Due</span>
          )}
          {candidate.occurrenceCount > 1 && (
            <span className="rw-pill" style={{ background: 'var(--df-bg-2, #ece5d3)', color: 'var(--df-primary, #1f5e3a)' }}>
              {candidate.occurrenceCount}x
            </span>
          )}
          {candidate.wouldCreateOt && (
            <span className="ot-meter">
              <span className="ot-meter-bar" style={{ width: 36 }} />
              <span style={{ fontSize: 10.5, fontWeight: 700, color: '#a9341d' }}>OT risk</span>
            </span>
          )}
        </div>
      )}

      {/* Row 4: Impacted days (if any) */}
      {candidate.impactedDays?.length > 0 && (
        <div style={{ fontSize: 10.5, color: 'var(--df-ink-4, #97a094)', marginBottom: 8 }}>
          Adds to:{' '}
          {candidate.impactedDays.map(day => (
            <span
              key={day.date}
              style={day.projectedOtMin > day.currentOtMin
                ? { color: '#a9341d', fontWeight: 700 }
                : undefined}
            >
              Wk {day.weekType} {formatMinutes(day.projectedTotalMin)}
              {day.projectedOtMin > 0 ? ` (OT ${formatMinutes(day.projectedOtMin)})` : ''}
              {' '}
            </span>
          ))}
        </div>
      )}

      {/* Row 5: Action buttons */}
      <div className="cand-actions">
        <button
          className="cand-btn focus-btn"
          onClick={() => handlePropertySelect(candidate.feature)}
        >
          <PlaceIcon style={{ fontSize: 14 }} />
          Focus
        </button>
        <button
          className="cand-btn add-btn"
          onClick={() => addToDay(candidate.aspirePropertyId)}
          disabled={!selectedScenarioId || routeActionPending}
        >
          {routeActionPending
            ? <CircularProgress size={14} style={{ color: '#fff' }} />
            : <AddCircleOutlineIcon style={{ fontSize: 14 }} />
          }
          Add to Day
        </button>
      </div>
    </div>
  )
}
